import ImageLayer from 'ol/layer/image'
import TileLayer from 'ol/layer/tile'
import ImageWMSSource from 'ol/source/imagewms'
import TileWMSSource from 'ol/source/tilewms'
import tileGrid from 'ol/tilegrid'
import '../../../Format'
import './Base'

/** @namespace GIS.Map.Layer.OL3 */
NS("GIS.Map.Layer.OL3");

/**
 * WMS layer.
 * Implements WMS Web Map service layer.
 *
 * @class
 * @extends GIS.Map.Layer.OL3.Base
 * todo add support of vendor options for common map servers
 */
GIS.Map.Layer.OL3.WMS = Class(GIS.Map.Layer.OL3.Base, /** @lends GIS.Map.Layer.OL3.WMS.prototype */{
    // options
    singleTile: false,
    tileSize: [256, 256],
    preload: 0,
    wrapX: true,
    url: null,
    crossOrigin: "anonymous",
    format: GIS.Format.PNG,
    /** @type {string[]} Additional url tokens */
    urlParams: [],
    params: {
        version: "1.3.0",
        layers: null,
        format: GIS.Format.PNG,
        styles: null,
        sld: null,
        transparent: true,
        filter: null
    },
    info: {
        query_layers: null,
        info_format: GIS.Format.HTML
    },
    legend: {
        layer: null,
        format: GIS.Format.PNG,
        style: null,
        width: null,
        height: null
    },
    /**
     * @type {ol.source.TileWMS|ol.source.ImageWMS}
     * @protected
     */
    source: null,
    /**
     * @type {ol.layer.Tile|ol.layer.Image}
     * @protected
     */
    layer: null,
    /**
     * @type {GIS.Map.Layer.WMS}
     * @protected
     */
    base: null,
    /** @inheritDoc */
    optionsNames()
    {
        return this.ParentCall().concat(["singleTile", "tileSize", "preload", "wrapX", "url", "urlParams", "format",
                                         "crossOrigin", "params", "info", "legend"]);
    },
    /**
     * @inheritDoc
     */
    prepareOptions(options)
    {
        options = this.Parent(options);

        if (options.format != null) {
            _.set(options, "params.format", options.format);
        }

        if (options.tileSize != null && !_.isArray(options.tileSize)) {
            options.tileSize = [_.parseInt(options.tileSize)];
        }

        return options;
    },
    /**
     * @param {Object} options
     * @returns {ol.layer.Tile|ol.layer.Image}
     * @protected
     */
    createLayer(options)
    {
        const layerOptions = {
            ...Util.pickOptions(
                options,
                ["opacity", "extent", "minResolution", "maxResolution"]
            ),
            visible: !!options.visibility,
            source: this.createSource(options)
        };

        var layer;
        if (options.singleTile) {
            layer = new ImageLayer(layerOptions);
        } else {
            layer = new TileLayer(_.extend(layerOptions, Util.pickOptions(options, ["preload"])));
        }

        return layer;
    },
    /**
     * @param {Object} options
     * @returns {ol.source.TileWMS|ol.source.ImageWMS}
     * @protected
     */
    createSource(options)
    {
        const sourceOptions = Util.pickOptions(options, ["url", "projection", "params", "attributions",
                                                         "crossOrigin", "maxZoom", "wrapX"]);
        sourceOptions.url = this.Class.prepareURL(sourceOptions.url, options, options.urlParams);

        if (sourceOptions.params != null) {
            sourceOptions.params = prepareWMSParams(sourceOptions.params);
        }

        var source;
        if (options.singleTile) {
            source = new ImageWMSSource(sourceOptions);
        } else {
            sourceOptions.tileGrid = tileGrid.createXYZ({
                extent: this.map.getProjection().getExtent(),
                maxZoom: Util.coalesce(options.maxZoom, this.map.maxZoom),
                minZoom: Util.coalesce(options.minZoom, this.map.minZoom),
                tileSize: Util.coalesce(options.tileSize, [256, 256])
            });

            source = new TileWMSSource(sourceOptions);
        }

        this.source = source;

        return source;
    },
    /**
     * @param {Object} params
     * @returns {Promise}
     */
    GetMap(params)
    {
        return Promise.resolve(new NotImplementedError("not implemented"));
    },
    /**
     * Executes GetFeatureInfo request
     *
     * @param {number[]} coordinate
     * @param {Object} [options] Additional options
     * @returns {Promise}
     * todo read loaded data with ol.format ! use WMSGetFeatureInfo format
     */
    GetFeatureInfo(coordinate, options)
    {
        if (_.isEmpty(this.info.query_layers)) {
            return Promise.reject(new RuntimeError("Nothing to load"));
        }

        const reqOptions = {
            ...Util.pickOptions(options, ["dataType"]),
            url: this.getGetFeatureInfoURL(coordinate, options),
            dataType: GIS.Format.dataType(this.info.info_format)
        };

        return Promise.resolve(Xhr.request(reqOptions, noop));
    },
    /**
     * @returns {Promise}
     */
    GetCapabilities(params)
    {
        const options = {
            url: this.getGetCapabilitiesURL(params),
            dataType: GIS.Format.dataType(this.params.format)
        };

        return Promise.resolve(Xhr.request(options, noop));
    },
    /**
     * Returns WMS GetCapabilities url
     * @returns {string}
     */
    getGetCapabilitiesURL(params)
    {
        const sourceParams = this.source.getParams();
        params = {
            ..._.omit(prepareWMSParams(params), 'VERSION'),
            ..._.pick(sourceParams, 'SERVICE', 'VERSION'),
            REQUEST: 'GetCapabilities'
        };

        var urlParts = Util.String.urlParts(this.url);

        urlParts.search = Util.String.urlQueryString({
            ...Util.String.urlQueryParams(urlParts.search),
            params
        });

        return Util.String.urlBuild(urlParts);
    },
    /**
     * @param {number[]} coordinate
     * @param {Object} params
     * @returns {string}
     */
    getGetFeatureInfoURL(coordinate, params)
    {
        params = _.defaults(
            _.omit(prepareWMSParams(params), 'VERSION'),
            prepareWMSParams(this.info)
        );

        return this.source.getGetFeatureInfoUrl(coordinate, this.map.getResolution(), this.projection, params);
    },
    /**
     * @param {Object} params
     * @returns {string}
     */
    getGetLegendGraphicURL(params)
    {
        params = _.defaults(
            _.omit(prepareWMSParams(params), 'VERSION'),
            prepareWMSParams(this.legend)
        );

        const sourceParams = this.source.getParams();

        params = {
            ...params,
            ..._.pick(sourceParams, 'SERVICE', 'VERSION'),
            REQUEST: 'GetLegendGraphic',
            LAYER: Util.String.coalesce(params.LAYER, sourceParams.LAYERS),
            STYLE: Util.String.coalesce(params.STYLE, sourceParams.STYLES)
        };

        const urlParts = Util.String.urlParts(this.url);

        urlParts.search = Util.String.urlQueryString({
            ...Util.String.urlQueryParams(urlParts.search),
            params
        });

        return Util.String.urlBuild(urlParts);
    },
    /** @inheritDoc */
    loadFeatureData(coordinate, options) {
        return this.GetFeatureInfo(coordinate, options);
    },
    /** @inheritDoc */
    setVendorProjection(projection)
    {
        // ol3 doesn't exports setProjection method in current version then recreate source of the layer
        if (this.layer) {
            this.layer.setSource(
                this.createSource({
                    ...Util.pickOptions(this, this.optionsNames()),
                    projection
                })
            );
        }
    },

    prepareWMSParams
});

GIS.Map.Layer.OL3.WMS.Static(/** @lends GIS.Map.Layer.OL3.WMS */{
    prepareWMSParams
});

/**
 * Prepares WMS request params.
 * @param {Object} params
 * @returns {Object}
 * @static
 * @public
 * todo convert filters
 */
function prepareWMSParams(params)
{
    const wmsParams = ['layers', 'version', 'format', 'styles', 'sld', 'transparent', 'filter', 'query_layers',
                       'info_format', 'layer', 'style', 'width', 'height'];
    const formatParams = ['format', 'info_format'];

    return _.chain(params).pickBy(value => Util.validateOption(value) != null)
        .reduce((result, value, name) => {
            if (_.includes(formatParams, name) && Util.isNumeric(value)) {
                value = GIS.Format.format(value);
            }

            if (_.includes(wmsParams, name)) {
                name = name.toUpperCase();
            }

            result[name] = Util.coalesce(value);

            return result;
        }, {}).value();
}
